(() => {
  if (window.mmCourseCheckoutInitialized) return;
  const configNode = document.getElementById('mm-course-checkout-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  if (!config.productId || !Array.isArray(config.variantIds)) return;
  window.mmCourseCheckoutInitialized = true;

  const variants = new Set(config.variantIds.map(String));
  // No early-expiry waiver is collected. Purchase never implies consent.
  const legacyConsentKeys = [
    'Digitalinhalt Sofortbeginn', 'Digitalinhalt Zustimmung Zeitpunkt',
    'Digitalinhalt Zustimmung Wortlautversion', 'Digitalinhalt Zustimmung Geltungsbereich',
    'Digitalinhalt Zustimmung Text', 'Digitalinhalt Zustimmung Quelle',
  ];
  const cartPath = `${window.Shopify?.routes?.root || '/'}cart`;
  let busy = false;
  let checkoutHelp;
  let operationCounter = 0;
  // On an ambiguous write, never send another add until a fresh cart can be read.
  let uncertainWrite = false;
  const buttonState = new Map();

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const flow = (stage, form, operationId) => emit('mm:checkout-flow', {
    stage, formId: form?.id || '', variantId: form?.querySelector('[name="id"]')?.value || '',
    source: 'course-checkout', operationId: operationId || `course-${Date.now()}-${++operationCounter}`,
  });
  const isCourseForm = (form) => form instanceof HTMLFormElement
    && new URL(form.action, location.origin).pathname.endsWith('/cart/add')
    && variants.has(String(form.querySelector('[name="id"]')?.value))
    && Boolean(form.querySelector('button[type="submit"], input[type="submit"]'));
  const forms = () => Array.from(document.forms).filter(isCourseForm);

  function showError(message, form, stage = 'purchase_error', operationId = '') {
    initialize();
    const error = checkoutHelp?.querySelector('[data-mm-course-checkout-error]');
    if (error) {
      error.textContent = `${message} `;
      const link = document.createElement('a');
      link.href = cartPath;
      link.textContent = 'Warenkorb prüfen';
      error.append(link);
      error.hidden = false;
      error.focus();
    }
    flow(stage, form, operationId);
  }

  function initialize() {
    const candidates = forms();
    candidates.forEach(form => { form.dataset.mmCourseCheckoutForm = 'true'; });
    if (!candidates.length || checkoutHelp?.isConnected) return;
    const primary = candidates.find(form => form.closest('[id^="ProductInfo-"]')) || candidates[0];
    checkoutHelp = document.createElement('div');
    checkoutHelp.className = 'mm-course-checkout-help';
    checkoutHelp.dataset.mmCourseCheckoutHelp = 'true';
    const policy = document.createElement('a');
    policy.href = config.policyUrl || '/policies/refund-policy';
    policy.textContent = 'Informationen zum Widerrufsrecht';
    policy.className = 'mm-course-checkout-help__policy';
    const error = document.createElement('p');
    error.className = 'mm-survey-direct-checkout__error';
    error.dataset.mmCourseCheckoutError = 'true';
    error.id = 'mm-course-checkout-error';
    error.hidden = true;
    error.tabIndex = -1;
    error.setAttribute('role', 'alert');
    checkoutHelp.append(policy, error);
    const button = primary.querySelector('button[type="submit"], input[type="submit"]');
    button.insertAdjacentElement('afterend', checkoutHelp);
  }

  function lock(value) {
    busy = value;
    if (value) {
      const buttons = [...forms().flatMap(form => Array.from(form.querySelectorAll('[type="submit"]'))),
        ...document.querySelectorAll('[data-mm-mobile-course-cta-button]')];
      buttons.forEach(button => {
        if (!buttonState.has(button)) buttonState.set(button, {
          disabled: button.disabled, busy: button.getAttribute('aria-busy'),
        });
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });
    } else {
      buttonState.forEach((state, button) => {
        button.disabled = state.disabled;
        if (state.busy === null) button.removeAttribute('aria-busy');
        else button.setAttribute('aria-busy', state.busy);
      });
      buttonState.clear();
    }
  }

  async function request(path, options = {}) {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 20000);
    try {
      const response = await fetch(path, {
        credentials: 'same-origin', cache: 'no-store', ...options, signal: abort.signal,
        headers: { Accept: 'application/json', ...(options.headers || {}) },
      });
      const data = await response.json();
      if (!response.ok || Number(data.status) >= 400) throw new Error('cart_request_failed');
      return data;
    } finally { clearTimeout(timeout); }
  }

  async function readCart() {
    const cart = await request(`${cartPath}.js`);
    if (!Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) throw new Error('invalid_cart');
    return cart;
  }

  function selection(form) {
    const body = new FormData(form);
    const variantId = String(body.get('id') || '');
    const rawQuantity = body.get('quantity') || '1';
    const quantity = Number(rawQuantity);
    if (!variants.has(variantId) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('invalid_selection');
    const properties = {};
    for (const [key, value] of body.entries()) {
      if (key.startsWith('properties[') && key.endsWith(']')) {
        if (typeof value !== 'string') throw new Error('unsupported_upload');
        if (value !== '') properties[key.slice(11, -1)] = value;
      }
    }
    return { body, variantId, quantity, properties, sellingPlan: String(body.get('selling_plan') || '') };
  }

  function matchingQuantity(cart, selected) {
    return cart.items.filter(item => {
      if (String(item.variant_id || item.id) !== selected.variantId) return false;
      const sellingPlan = String(item.selling_plan_allocation?.selling_plan?.id || '');
      if (sellingPlan !== selected.sellingPlan) return false;
      const actual = Object.fromEntries(Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value != null));
      const keys = Object.keys(selected.properties);
      return keys.length === Object.keys(actual).length && keys.every(key => String(actual[key]) === selected.properties[key]);
    }).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  async function ensureSelection(cart, selected, form, operationId) {
    const before = matchingQuantity(cart, selected);
    if (before >= selected.quantity) return cart;
    const body = selected.body;
    body.set('quantity', String(selected.quantity - before));
    // Navigation/attributes are handled only after Shopify confirms the cart.
    for (const key of [...body.keys()]) {
      if (key === 'return_to' || key === 'sections' || key === 'sections_url' || key.startsWith('attributes[')) body.delete(key);
    }
    uncertainWrite = true;
    try {
      await request(`${cartPath}/add.js`, { method: 'POST', body });
    } catch (_) {
      // Shopify may have changed the cart even after a timeout or 422 response.
      // The following read is the only recovery, not an automatic second add.
    }
    const updated = await readCart();
    uncertainWrite = false;
    const after = matchingQuantity(updated, selected);
    if (after > before) emit('mm:cart-add-confirmed', {
      variantId: selected.variantId, quantity: after - before,
      source: 'course-checkout', formId: form.id || '', operationId,
    });
    if (after < selected.quantity) throw new Error('cart_quantity_not_reached');
    return updated;
  }

  function digitalScope(cart) {
    const entries = new Map();
    cart.items.filter(item => item.requires_shipping === false).forEach(item => {
      const product = String(item.product_id || '');
      const variant = String(item.variant_id || item.id);
      const key = `${product}:${variant}`;
      const entry = entries.get(key) || { product, variant, quantity: 0 };
      entry.quantity += Number(item.quantity);
      entries.set(key, entry);
    });
    return [...entries].sort(([a], [b]) => a.localeCompare(b)).map(([, entry]) => entry);
  }

  async function prepareCheckout(cart, selected) {
    const scope = digitalScope(cart);
    if (!scope.some(item => item.variant === selected.variantId)) throw new Error('course_missing_from_cart');
    const attributes = Object.fromEntries(legacyConsentKeys
      .filter(key => cart.attributes?.[key] != null && cart.attributes[key] !== '')
      .map(key => [key, '']));
    const campaignCode = window.MMAutumnOffer?.getCode?.(selected.variantId) || '';
    const code = campaignCode || window.MMSurveyReward?.getCode?.() || '';
    const payload = {};
    if (Object.keys(attributes).length) payload.attributes = attributes;
    if (code) {
      const currentCodes = (cart.discount_codes || []).filter(entry => entry.applicable).map(entry => entry.code);
      // An explicitly chosen campaign has a non-combinable offer. Keep the
      // existing survey behavior for all other visits and courses.
      payload.discount = campaignCode || [...new Set([...currentCodes, code])].join(',');
    }
    if (!Object.keys(payload).length) return cart;
    const updated = await request(`${cartPath}/update.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!Array.isArray(updated.items) || !Number.isFinite(updated.total_price)
      || matchingQuantity(updated, selected) < selected.quantity
      || legacyConsentKeys.some(key => updated.attributes?.[key] != null && updated.attributes[key] !== '')) {
      throw new Error('checkout_preparation_not_confirmed');
    }
    if (code && !updated.discount_codes?.some(entry => entry.code?.toUpperCase() === code.toUpperCase() && entry.applicable)) {
      throw new Error('reward_not_applicable');
    }
    // Preserve protection against concurrent changes to digital items.
    const updatedScope = digitalScope(updated);
    if (JSON.stringify(updatedScope) !== JSON.stringify(scope)) throw new Error('cart_changed');
    return updated;
  }

  async function checkout(form) {
    if (busy) return;
    initialize();
    const error = checkoutHelp?.querySelector('[data-mm-course-checkout-error]');
    if (error) error.hidden = true;
    const operationId = `course-${Date.now()}-${++operationCounter}`;
    lock(true);
    try {
      const selected = selection(form);
      const cart = await readCart();
      // A fresh successful read resolves any ambiguous earlier write.
      uncertainWrite = false;
      const withProduct = await ensureSelection(cart, selected, form, operationId);
      await prepareCheckout(withProduct, selected);
      flow('checkout_redirect', form, operationId);
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
      // Lock intentionally remains until page navigation or pageshow after Back.
    } catch (error) {
      const messages = {
        reward_not_applicable: 'Der vorgemerkte Gutschein ist für diesen Warenkorb nicht anwendbar. Bitte prüfe dort Preis und Gutschein, bevor du zur Kasse gehst.',
        invalid_selection: 'Bitte wähle eine gültige Kursvariante und Menge.',
        cart_changed: 'Dein Warenkorb wurde zwischenzeitlich geändert. Bitte prüfe ihn erneut.',
      };
      showError(messages[error.message] || (uncertainWrite
        ? 'Die Rückmeldung zum Warenkorb fehlt. Bitte prüfe den Warenkorb; der Kurs könnte bereits enthalten sein.'
        : 'Der Checkout konnte noch nicht sicher vorbereitet werden. Dein Warenkorb bleibt erhalten. Bitte versuche es erneut oder prüfe ihn.'), form, 'purchase_error', operationId);
      lock(false);
    }
  }

  document.addEventListener('submit', event => {
    if (!isCourseForm(event.target)) return;
    event.preventDefault();
    checkout(event.target);
  }, true);
  window.addEventListener('pageshow', () => {
    lock(false);
  });
  initialize();
  new MutationObserver(initialize).observe(document.body, { childList: true, subtree: true });
})();
